import type {
  BlockType,
  ComponentLoader,
  DesignSystem,
  ExpansionPageKey,
  HostTheme,
  ThemeHost,
  ThemeRegistration,
} from './types';

const FALLBACK_THEME = 'default';

/**
 * Which component draws what, per theme and per host. A theme uses its own component, then its parent's; the parent
 * is a fallback inside one host only, so a theme registered for pages does not become a project theme through it.
 *
 * Nothing fetched at runtime changes what this resolves: it is the only source of what a visitor sees.
 */
export class ThemeRegistry {
  private themes = new Map<string, ThemeRegistration>();

  register(key: string, registration: ThemeRegistration): void {
    if (registration.parent !== undefined) {
      const parent = this.themes.get(registration.parent);
      if (!parent) {
        throw new Error(`Theme "${key}": its parent "${registration.parent}" is not registered`);
      }
      if (parent.parent !== undefined) {
        throw new Error(
          `Theme "${key}": its parent "${registration.parent}" has a parent, and a parent must be a base theme`,
        );
      }
    }
    this.themes.set(key, registration);
  }

  // Whether the theme registers itself for this host. A theme that does not resolves there to the fallback theme.
  covers(host: ThemeHost, themeKey: string): boolean {
    return Boolean(this.themes.get(themeKey)?.[host]);
  }

  // The theme's parent: null for a base theme, undefined for a theme that is not registered.
  getParent(themeKey: string): string | null | undefined {
    const registration = this.themes.get(themeKey);
    return registration ? (registration.parent ?? null) : undefined;
  }

  getLayout(host: ThemeHost, themeKey: string): ComponentLoader {
    return this.resolve(host, themeKey).theme.layout;
  }

  getDesignSystem(host: ThemeHost, themeKey: string): DesignSystem {
    return this.resolve(host, themeKey).theme.designSystem;
  }

  /**
   * The component of a block type, or null when neither the theme nor its parent has one: the caller skips the block
   * and renders the rest. In development it throws instead, so the gap is seen where it is introduced.
   */
  getBlockComponent(host: ThemeHost, themeKey: string, blockType: BlockType): ComponentLoader | null {
    const loader = this.lookUp(host, themeKey, (theme) => theme.blockRenderers[blockType]);
    if (loader) {
      return loader;
    }

    const message = `No ${host} component for block type ${blockType} in theme "${themeKey}" or its parent`;
    if (import.meta.env.DEV) {
      throw new Error(message);
    }
    console.warn(`${message}: the block is skipped`);
    return null;
  }

  // Whether a block of this type is drawn in this theme; a layout leaves out the blocks that are not. Like
  // `getBlockComponent`, it throws in development when neither the theme nor its parent has one.
  hasBlockComponent(host: ThemeHost, themeKey: string, blockType: BlockType): boolean {
    return this.getBlockComponent(host, themeKey, blockType) !== null;
  }

  getExpansionPageComponent(host: ThemeHost, themeKey: string, pageKey: ExpansionPageKey): ComponentLoader {
    const loader = this.lookUp(host, themeKey, (theme) => theme.expansionPageRenderers?.[pageKey]);
    if (!loader) {
      throw new Error(`No ${host} expansion page "${pageKey}" in theme "${themeKey}" or its parent`);
    }
    return loader;
  }

  // The theme's own registration for this host, or the fallback theme's when it has none.
  private resolve(host: ThemeHost, themeKey: string): { registration: ThemeRegistration; theme: HostTheme } {
    const registration = this.themes.get(themeKey);
    if (registration?.[host]) {
      return { registration, theme: registration[host] };
    }

    console.warn(`Theme "${themeKey}" not found for ${host}, falling back to "${FALLBACK_THEME}"`);
    const fallback = this.themes.get(FALLBACK_THEME);
    if (!fallback?.[host]) {
      throw new Error(`The fallback theme "${FALLBACK_THEME}" is not registered for ${host}`);
    }
    return { registration: fallback, theme: fallback[host] };
  }

  // The theme's own component, then its parent's, in the same host.
  private lookUp(
    host: ThemeHost,
    themeKey: string,
    pick: (theme: HostTheme) => ComponentLoader | undefined,
  ): ComponentLoader | undefined {
    const { registration, theme } = this.resolve(host, themeKey);
    const own = pick(theme);
    if (own) {
      return own;
    }

    const parent = registration.parent !== undefined ? this.themes.get(registration.parent)?.[host] : undefined;
    return parent ? pick(parent) : undefined;
  }
}
